import { CrmTaskStatus } from '@plato/data/prisma';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import { cn } from '@plato/ui/util/classes';
import { CircleCheckIcon, CircleIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CrmLinkModelType, CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { TaskCreatedSource } from '../../analytics/types/analytics-events.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import { useShowCreateTaskDialog } from '../hooks/use-show-create-task-dialog.ts';
import { buildTaskListUrl, type TaskListFilters } from '../hooks/use-task-filters-state.ts';

export type TaskColumnLink = {
    modelType: CrmLinkModelType;
    modelId: string;
    title: string;
};

export type TaskColumnCellProps = {
    links?: TaskColumnLink[];
    tasks?: CrmTaskDTO[];
    assignedToId?: string;
    source: TaskCreatedSource;
};

function getTaskListFilters(modelType: CrmLinkModelType, modelId: string): Partial<TaskListFilters> {
    switch (modelType) {
        case 'customer':
            return { customerIds: [modelId] };
        case 'sales_order':
            return { salesOrderIds: [modelId] };
        case 'sales_quote':
            return { salesQuoteIds: [modelId] };
        default:
            return {};
    }
}

export function TaskColumnCell({
    links,
    tasks,
    assignedToId,
    source,
}: TaskColumnCellProps): React.ReactElement | null {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { openTaskDetails } = useDialogs();

    if (!tasks || tasks.length === 0) {
        return <NoTaskState links={links} assignedToId={assignedToId} source={source} />;
    }

    const hasOpenTask = tasks.some((task) => task.status === CrmTaskStatus.OPEN);

    if (tasks.length === 1) {
        const task = tasks[0];
        if (!task) {
            return null;
        }
        const isCompleted = task.status === CrmTaskStatus.COMPLETED;

        return (
            <Button
                variant="ghost"
                className="max-w-40"
                leftIcon={hasOpenTask ? CircleIcon : CircleCheckIcon}
                onClick={() => {
                    openTaskDetails(task.taskId);
                }}
            >
                <Text
                    size="sm"
                    className={cn('truncate', {
                        'line-through': isCompleted,
                    })}
                >
                    {task.description ?? t('table_header__task', { ns: 'tasks' })}
                </Text>
            </Button>
        );
    }

    const primaryLink = links?.[0];
    if (!primaryLink) {
        return null;
    }

    return (
        <Button
            variant="ghost"
            leftIcon={hasOpenTask ? CircleIcon : CircleCheckIcon}
            onClick={() => {
                void navigate(
                    buildTaskListUrl(getTaskListFilters(primaryLink.modelType, primaryLink.modelId)),
                );
            }}
        >
            <Text className="truncate" size="sm">
                {t('task_column__tasks_count', { ns: 'tasks', count: tasks.length })}
            </Text>
        </Button>
    );
}

type NoTaskStateProps = {
    links?: TaskColumnLink[];
    assignedToId?: string;
    source: TaskCreatedSource;
};

function NoTaskState({ links, assignedToId, source }: NoTaskStateProps): React.ReactElement {
    const { t } = useTranslation();
    const showCreateTaskDialog = useShowCreateTaskDialog();

    return (
        <Tooltip>
            <TooltipTrigger
                render={
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                            showCreateTaskDialog({
                                links,
                                assignedToId,
                                source,
                            });
                        }}
                        icon={PlusIcon}
                    />
                }
            />
            <TooltipContent>{t('task_column__create_task_tooltip', { ns: 'tasks' })}</TooltipContent>
        </Tooltip>
    );
}
